import { Alert02Icon, Cancel01Icon, CancelCircleIcon, CheckmarkCircle02Icon, InformationCircleIcon } from '@hugeicons/core-free-icons';
import { Icon } from './icon.js';
import { Toast as ToastPrimitive } from 'radix-ui';
import * as React from 'react';
import { cn } from '../../lib/cn.js';

export type ToastTone = 'info' | 'success' | 'warning' | 'danger';

export interface ToastInput {
  title: string;
  description?: string;
  tone?: ToastTone;
  durationMs?: number;
  /** Optional action, e.g. { label: 'Deshacer', onClick }. */
  action?: { label: string; onClick: () => void };
}

interface ToastItem extends ToastInput {
  id: number;
}

const ToastContext = React.createContext<((t: ToastInput) => void) | null>(null);

const ICONS: Record<ToastTone, React.ReactNode> = {
  info: <Icon icon={InformationCircleIcon} aria-hidden className="text-info" />,
  success: <Icon icon={CheckmarkCircle02Icon} aria-hidden className="text-success" />,
  warning: <Icon icon={Alert02Icon} aria-hidden className="text-warning" />,
  danger: <Icon icon={CancelCircleIcon} aria-hidden className="text-danger" />,
};

const ACCENT: Record<ToastTone, string> = {
  info: 'border-l-info',
  success: 'border-l-success',
  warning: 'border-l-warning',
  danger: 'border-l-danger',
};

/** Mount once near the app root. Use `useToast()` to show notifications. */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = React.useState<ToastItem[]>([]);
  const counter = React.useRef(0);
  const push = React.useCallback((t: ToastInput) => {
    counter.current += 1;
    const id = counter.current;
    setItems((prev) => [...prev.slice(-3), { ...t, id }]);
  }, []);
  return (
    <ToastContext.Provider value={push}>
      <ToastPrimitive.Provider swipeDirection="right" label="Notificaciones">
        {children}
        {items.map((t) => {
          const tone = t.tone ?? 'info';
          return (
            <ToastPrimitive.Root
              key={t.id}
              duration={t.durationMs ?? 5000}
              type={tone === 'danger' ? 'foreground' : 'background'}
              onOpenChange={(open) => {
                if (!open) setItems((prev) => prev.filter((x) => x.id !== t.id));
              }}
              className={cn(
                'flex items-start gap-3 rounded-menu border border-l-4 border-border-subtle bg-surface-default p-4 shadow-overlay animate-slide-in-right [&_svg]:size-5 [&_svg]:shrink-0',
                ACCENT[tone],
              )}
            >
              {ICONS[tone]}
              <div className="grid flex-1 gap-0.5">
                <ToastPrimitive.Title className="text-body font-semibold text-text-primary">{t.title}</ToastPrimitive.Title>
                {t.description ? <ToastPrimitive.Description className="text-body text-text-secondary">{t.description}</ToastPrimitive.Description> : null}
                {t.action ? (
                  <ToastPrimitive.Action
                    altText={t.action.label}
                    onClick={t.action.onClick}
                    className="mt-1 justify-self-start rounded-control text-body font-medium text-brand-primary underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-focus"
                  >
                    {t.action.label}
                  </ToastPrimitive.Action>
                ) : null}
              </div>
              <ToastPrimitive.Close
                aria-label="Cerrar notificación"
                className="rounded-control p-1 text-text-secondary hover:bg-surface-subtle focus-visible:outline-2 focus-visible:outline-focus"
              >
                <Icon icon={Cancel01Icon} aria-hidden className="!size-4" />
              </ToastPrimitive.Close>
            </ToastPrimitive.Root>
          );
        })}
        <ToastPrimitive.Viewport className="fixed bottom-4 right-4 z-[60] flex w-[min(380px,calc(100vw-32px))] flex-col gap-2 outline-none" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}

export function useToast(): { toast: (t: ToastInput) => void } {
  const push = React.useContext(ToastContext);
  if (!push) throw new Error('useToast debe usarse dentro de <ToastProvider>');
  return { toast: push };
}
